'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { trackOpenAiEvent } from '@/lib/analytics/openai-pixel'

/**
 * OpenAI Ads pixel SPA page-view tracking.
 *
 * The optional-cookie bootstrap fires `page_viewed` once for the initial
 * document. App Router client-side navigations never re-execute it, so this
 * component records subsequent page views.
 *
 * There is no env check here: `OPEN_AI_PIXEL` is deliberately not a
 * `NEXT_PUBLIC_` variable, so it is unreadable from the browser bundle. The
 * gate is `trackOpenAiEvent`'s consent check plus `window.oaiq` feature
 * detection. The bootstrap defines the queue stub synchronously.
 */
export function OpenAiPixel() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPathRef = useRef<string | null>(null)

  useEffect(() => {
    if (!pathname) return
    const search = searchParams?.toString()
    const fullPath = search ? `${pathname}?${search}` : pathname

    // First run is the initial document — the install snippet already fired its
    // page_viewed, so record the path and bail rather than double-counting it.
    if (lastPathRef.current === null) {
      lastPathRef.current = fullPath
      return
    }

    // Guard against re-renders that don't actually change the URL (searchParams
    // identity can churn) turning into duplicate pixel hits.
    if (lastPathRef.current === fullPath) return
    lastPathRef.current = fullPath

    trackOpenAiEvent('page_viewed')
  }, [pathname, searchParams])

  return null
}
